import { useState, useEffect, useRef, useMemo } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { COMMUNITY_FEED_ENABLED } from "@/lib/community-launch";
import { CommunityComingSoonPage } from "@/pages/CommunityComingSoonPage";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { TagChip } from "@/components/TagChip";
import { QuoteCard } from "@/components/QuoteCard";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGetCommunityQuotes,
  useGetTags,
  useVoteQuote,
  useDeleteQuoteVote,
  getGetCommunityQuotesQueryKey,
  getGetTagsQueryKey,
  type CommunityQuote,
} from "@workspace/api-client-react";

const TOP_TAG_COUNT = 10;
const PAGE_SIZE = 20;

interface CardQuote {
  id: number;
  text: string;
  author: string;
  era?: string | null;
  year?: string | null;
  tags: string[];
}

function toCardQuote(q: CommunityQuote): CardQuote {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : null,
    tags: q.tags.map((t) => t.name),
  };
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

interface OptimisticVote {
  upvotes: number;
  downvotes: number;
  userVote: "up" | "down" | null;
}

function VoteButtons({
  quote,
  optimistic,
  onVote,
  onRemoveVote,
  isAuthenticated,
  onGuestClick,
}: {
  quote: CommunityQuote;
  optimistic: OptimisticVote | null;
  onVote: (vote: "up" | "down") => void;
  onRemoveVote: () => void;
  isAuthenticated: boolean;
  onGuestClick: () => void;
}) {
  const upvotes = optimistic?.upvotes ?? quote.upvotes;
  const downvotes = optimistic?.downvotes ?? quote.downvotes;
  const userVote = optimistic !== null ? optimistic.userVote : quote.userVote;
  const netScore = upvotes - downvotes;

  function handleUp() {
    if (!isAuthenticated) { onGuestClick(); return; }
    if (userVote === "up") { onRemoveVote(); } else { onVote("up"); }
  }

  function handleDown() {
    if (!isAuthenticated) { onGuestClick(); return; }
    if (userVote === "down") { onRemoveVote(); } else { onVote("down"); }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleUp}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition active:scale-[0.95] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 ${
          userVote === "up"
            ? "bg-emerald-100 text-emerald-700"
            : "text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        }`}
        aria-label="Upvote"
        title={userVote === "up" ? "Remove upvote" : "Upvote"}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
        <span>{upvotes}</span>
      </button>

      <span
        className={`min-w-[2ch] text-center text-xs font-semibold tabular-nums ${
          netScore > 0 ? "text-emerald-600" : netScore < 0 ? "text-red-500" : "text-stone-400"
        }`}
      >
        {netScore > 0 ? `+${netScore}` : netScore}
      </span>

      <button
        onClick={handleDown}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition active:scale-[0.95] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 ${
          userVote === "down"
            ? "bg-red-100 text-red-600"
            : "text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        }`}
        aria-label="Downvote"
        title={userVote === "down" ? "Remove downvote" : "Downvote"}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
        <span>{downvotes}</span>
      </button>
    </div>
  );
}

export function CommunityPage() {
  return COMMUNITY_FEED_ENABLED ? <CommunityFeed /> : <CommunityComingSoonPage />;
}

function CommunityFeed() {
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [sort, setSort] = useState<"new" | "top">("new");
  const [page, setPage] = useState(1);
  const [allQuotes, setAllQuotes] = useState<CommunityQuote[]>([]);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [optimisticVotes, setOptimisticVotes] = useState<Map<number, OptimisticVote>>(new Map());
  const [showSignInNudge, setShowSignInNudge] = useState(false);

  usePageMeta({
    title: "Community Quotes — QuotedsArrow",
    description:
      "Browse, vote, and discover quotes submitted by the QuotedsArrow community.",
  });

  const { currentUser, signInWithGoogle } = useAuth();
  const isAuthenticated = !!currentUser;

  const voteMutation = useVoteQuote();
  const deleteMutation = useDeleteQuoteVote();

  const filterKey = `${activeTag ?? ""}:${sort}`;
  const trackedKeyRef = useRef(filterKey);
  if (trackedKeyRef.current !== filterKey) {
    trackedKeyRef.current = filterKey;
    setPage(1);
    setAllQuotes([]);
    setOptimisticVotes(new Map());
  }

  const tagsQuery = useGetTags({
    query: { queryKey: getGetTagsQueryKey() },
  });

  const allTags = useMemo(() => {
    return [...(tagsQuery.data ?? [])];
  }, [tagsQuery.data]);

  const visibleTags = useMemo(() => {
    if (tagsExpanded) return allTags;
    const top = allTags.slice(0, TOP_TAG_COUNT);
    if (activeTag && !top.some((t) => t.name === activeTag)) {
      const found = allTags.find((t) => t.name === activeTag);
      if (found) return [...top, found];
    }
    return top;
  }, [tagsExpanded, allTags, activeTag]);

  const queryParams = {
    page,
    pageSize: PAGE_SIZE,
    sort,
    ...(activeTag ? { tag: activeTag } : {}),
  };

  const communityQuery = useGetCommunityQuotes(queryParams, {
    query: { queryKey: getGetCommunityQuotesQueryKey(queryParams) },
  });

  const prevDataRef = useRef<typeof communityQuery.data>(undefined);
  useEffect(() => {
    if (!communityQuery.data || communityQuery.data === prevDataRef.current) return;
    prevDataRef.current = communityQuery.data;
    if (communityQuery.data.page === 1) {
      setAllQuotes(communityQuery.data.data);
    } else {
      setAllQuotes((prev) => [...prev, ...communityQuery.data!.data]);
    }
  }, [communityQuery.data]);

  const total = communityQuery.data?.total ?? 0;
  const hasMore = allQuotes.length < total;
  const isLoading = communityQuery.isLoading && page === 1;

  function handleTagToggle(tagName: string) {
    setActiveTag((prev) => (prev === tagName ? null : tagName));
  }

  function handleVote(quote: CommunityQuote, vote: "up" | "down") {
    const current = optimisticVotes.get(quote.id) ?? {
      upvotes: quote.upvotes,
      downvotes: quote.downvotes,
      userVote: quote.userVote,
    };

    let newUpvotes = current.upvotes;
    let newDownvotes = current.downvotes;

    if (current.userVote === "up") newUpvotes--;
    if (current.userVote === "down") newDownvotes--;
    if (vote === "up") newUpvotes++;
    else newDownvotes++;

    setOptimisticVotes((prev) =>
      new Map(prev).set(quote.id, { upvotes: newUpvotes, downvotes: newDownvotes, userVote: vote }),
    );

    voteMutation.mutate(
      { id: quote.id, data: { vote } },
      {
        onSuccess: (data) => {
          setOptimisticVotes((prev) => {
            const next = new Map(prev);
            next.set(quote.id, {
              upvotes: data.upvotes,
              downvotes: data.downvotes,
              userVote: data.userVote,
            });
            return next;
          });
        },
        onError: () => {
          setOptimisticVotes((prev) => {
            const next = new Map(prev);
            next.delete(quote.id);
            return next;
          });
        },
      },
    );
  }

  function handleRemoveVote(quote: CommunityQuote) {
    const current = optimisticVotes.get(quote.id) ?? {
      upvotes: quote.upvotes,
      downvotes: quote.downvotes,
      userVote: quote.userVote,
    };

    let newUpvotes = current.upvotes;
    let newDownvotes = current.downvotes;
    if (current.userVote === "up") newUpvotes--;
    if (current.userVote === "down") newDownvotes--;

    setOptimisticVotes((prev) =>
      new Map(prev).set(quote.id, { upvotes: newUpvotes, downvotes: newDownvotes, userVote: null }),
    );

    deleteMutation.mutate(
      { id: quote.id },
      {
        onSuccess: () => {
          // Vote confirmed — optimistic state already reflects the change
        },
        onError: () => {
          setOptimisticVotes((prev) => {
            const next = new Map(prev);
            next.delete(quote.id);
            return next;
          });
        },
      },
    );
  }

  return (
    <section data-testid="page-community">
      <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="mb-1 text-sm font-semibold text-stone-700">Community Quotes</p>
        <p className="text-xs leading-relaxed text-stone-500">
          Quotes submitted and approved from the community. Browse, save, and discover words from fellow readers.
        </p>
      </div>

      {showSignInNudge && (
        <div className="mb-5 rounded-3xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold text-amber-800">Sign in to vote</p>
          <p className="mb-3 text-xs text-amber-700">
            Create a free account to upvote or downvote community quotes.
          </p>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                setShowSignInNudge(false);
                await signInWithGoogle();
              }}
              className="rounded-2xl bg-amber-800 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-amber-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1"
            >
              Sign in with Google
            </button>
            <button
              onClick={() => setShowSignInNudge(false)}
              className="rounded-2xl px-4 py-1.5 text-xs font-medium text-amber-700 transition hover:bg-amber-100 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="mb-5 flex items-center gap-2">
        <span className="text-xs font-medium text-stone-500">Sort:</span>
        <div className="flex rounded-full border border-stone-200 bg-white p-0.5 shadow-sm">
          {(["new", "top"] as const).map((option) => (
            <button
              key={option}
              onClick={() => setSort(option)}
              className={`rounded-full px-4 py-1 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 ${
                sort === option
                  ? "bg-stone-900 text-white"
                  : "text-stone-500 hover:text-stone-800"
              }`}
            >
              {option === "new" ? "Newest" : "Top"}
            </button>
          ))}
        </div>
      </div>

      {allTags.length > 0 && (
        <div className="mb-5 rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-sm font-semibold text-stone-700">Filter by tag</p>
          <div className="flex flex-wrap gap-2">
            {visibleTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagToggle(tag.name)}
                className="rounded-full transition active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
              >
                <TagChip active={activeTag === tag.name}>{tag.name}</TagChip>
              </button>
            ))}
          </div>
          {allTags.length > TOP_TAG_COUNT && (
            <button
              onClick={() => setTagsExpanded((e) => !e)}
              className="mt-2 text-xs font-medium text-stone-500 transition hover:text-stone-800"
            >
              {tagsExpanded ? "Show less" : `Show all ${allTags.length}`}
            </button>
          )}
        </div>
      )}

      {!isLoading && allQuotes.length > 0 && (
        <p className="mb-3 text-sm text-stone-500">
          Showing{" "}
          <span className="font-medium text-stone-700">{allQuotes.length}</span>
          {total > allQuotes.length ? (
            <>
              {" "}of{" "}
              <span className="font-medium text-stone-700">{total}</span>
            </>
          ) : null}{" "}
          community quote{total !== 1 ? "s" : ""}
          {activeTag ? (
            <>
              {" "}tagged{" "}
              <span className="font-medium text-stone-700">"{activeTag}"</span>
            </>
          ) : null}
        </p>
      )}

      {isLoading ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="animate-pulse text-sm text-stone-400">Loading community quotes…</p>
        </div>
      ) : allQuotes.length === 0 ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">
            {activeTag
              ? `No community quotes tagged "${activeTag}".`
              : "No community quotes yet — submit the first one from your profile!"}
          </p>
          {activeTag && (
            <button
              className="mt-4 rounded text-sm font-medium text-stone-900 underline underline-offset-4 transition hover:text-stone-600 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
              onClick={() => setActiveTag(null)}
            >
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {allQuotes.map((quote) => (
              <div key={quote.id} className="flex flex-col gap-1.5">
                <QuoteCard quote={toCardQuote(quote)} enableFlip />
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-1">
                  <p className="min-w-0 truncate text-xs text-stone-400">
                    {quote.submittedByUsername ? (
                      <>
                        Submitted by{" "}
                        <span className="font-medium text-stone-500">
                          @{quote.submittedByUsername}
                        </span>
                      </>
                    ) : (
                      "Community submission"
                    )}
                    {" · "}
                    {formatDate(quote.createdAt)}
                  </p>
                  <VoteButtons
                    quote={quote}
                    optimistic={optimisticVotes.get(quote.id) ?? null}
                    onVote={(vote) => handleVote(quote, vote)}
                    onRemoveVote={() => handleRemoveVote(quote)}
                    isAuthenticated={isAuthenticated}
                    onGuestClick={() => setShowSignInNudge(true)}
                  />
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={communityQuery.isFetching}
                className="rounded-2xl border border-stone-200 bg-white px-7 py-2.5 text-sm font-medium text-stone-700 shadow-sm transition hover:bg-stone-50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50"
              >
                {communityQuery.isFetching ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
